(function () {
  // Scene behavior and editor-selected assets are coordinated by this custom element.
  const SDK_URL = 'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v2.2.7/dist/unicornStudio.umd.js';

  function loadUnicornStudio() {
    if (window.UnicornStudio && typeof window.UnicornStudio.addScene === 'function') {
      return Promise.resolve(window.UnicornStudio);
    }

    if (window.pgUnicornStudioPromise) {
      return window.pgUnicornStudioPromise;
    }

    window.pgUnicornStudioPromise = new Promise((resolve, reject) => {
      let script = document.querySelector('script[data-pg-unicorn-sdk]');

      const handleLoad = () => {
        if (window.UnicornStudio && typeof window.UnicornStudio.addScene === 'function') {
          resolve(window.UnicornStudio);
        } else {
          reject(new Error('Unicorn Studio loaded without the expected API.'));
        }
      };

      if (!script) {
        script = document.createElement('script');
        script.src = SDK_URL;
        script.async = true;
        script.dataset.pgUnicornSdk = '';
        document.head.appendChild(script);
      }

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', () => reject(new Error('Unable to load Unicorn Studio.')), { once: true });
    });

    return window.pgUnicornStudioPromise;
  }

  class PGLiquidGlassHero extends HTMLElement {
    connectedCallback() {
      this.sceneContainer = this.querySelector('[data-pg-unicorn-scene]');
      this.deadzone = this.querySelector('[data-pg-unicorn-quiet-zone]');
      this.backgroundMediaQuery = window.matchMedia('(max-width: 575px)');
      this.handleBackgroundBreakpointChange = () => this.applyBackground();
      this.appliedBackgroundUrl = '';
      this.stopScenePointerTracking = (event) => {
        if (this.dataset.deadzoneEnabled !== 'true' || !this.deadzone) {
          return;
        }

        const pointer = event.touches?.[0] || event.changedTouches?.[0] || event;
        const deadzoneRect = this.deadzone.getBoundingClientRect();

        if (
          pointer.clientX >= deadzoneRect.left &&
          pointer.clientX <= deadzoneRect.right &&
          pointer.clientY >= deadzoneRect.top &&
          pointer.clientY <= deadzoneRect.bottom
        ) {
          event.stopPropagation();
        }
      };

      ['mousemove', 'pointermove', 'touchmove'].forEach((eventName) => {
        this.addEventListener(eventName, this.stopScenePointerTracking, { capture: true, passive: true });
      });

      if (typeof this.backgroundMediaQuery.addEventListener === 'function') {
        this.backgroundMediaQuery.addEventListener('change', this.handleBackgroundBreakpointChange);
      } else {
        this.backgroundMediaQuery.addListener(this.handleBackgroundBreakpointChange);
      }

      this.handleEntranceReady = () => {
        this.entranceFrame = window.requestAnimationFrame(() => this.initializeEntranceAnimation());
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleEntranceReady, { once: true });
      } else {
        this.handleEntranceReady();
      }

      this.initializeScene();
    }

    disconnectedCallback() {
      ['mousemove', 'pointermove', 'touchmove'].forEach((eventName) => {
        this.removeEventListener(eventName, this.stopScenePointerTracking, true);
      });

      if (typeof this.backgroundMediaQuery?.removeEventListener === 'function') {
        this.backgroundMediaQuery.removeEventListener('change', this.handleBackgroundBreakpointChange);
      } else {
        this.backgroundMediaQuery?.removeListener(this.handleBackgroundBreakpointChange);
      }

      document.removeEventListener('DOMContentLoaded', this.handleEntranceReady);
      window.cancelAnimationFrame(this.entranceFrame);
      this.introObserver?.disconnect();
      this.introTrigger?.kill();
      this.introTimeline?.revert();
      this.introObserver = null;
      this.introTrigger = null;
      this.introTimeline = null;

      this.scene?.destroy();
      this.scene = null;
    }

    async initializeScene() {
      if (!this.sceneContainer || !this.dataset.projectId || this.initializing) {
        return;
      }

      this.initializing = true;

      try {
        const UnicornStudio = await loadUnicornStudio();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.scene = await UnicornStudio.addScene({
          element: this.sceneContainer,
          projectId: this.dataset.projectId,
          fps: reduceMotion ? 24 : Number(this.dataset.fps || 60),
          scale: Number(this.dataset.scale || 0.85),
          dpi: Number(this.dataset.dpi || 1.5),
          lazyLoad: true,
          production: this.dataset.production === 'true',
          altText: 'Liquid glass Purica triskelion over an abstract organic background',
          ariaLabel: 'Decorative interactive Purica triskelion scene',
          interactivity: {
            mouse: {
              disableMobile: true,
              disabled: reduceMotion
            }
          }
        });

        this.backgroundLayer = this.scene.getLayer(this.dataset.backgroundLayerId);
        this.originalBackgroundUrl = this.backgroundLayer?.src || '';
        this.applyBackground();

        this.dataset.sceneReady = 'true';
      } catch (error) {
        this.dataset.sceneError = 'true';
        console.error('[PG Liquid glass hero]', error);
      } finally {
        this.initializing = false;
      }
    }

    getBackgroundUrl() {
      if (this.backgroundMediaQuery?.matches && this.dataset.backgroundMobileUrl) {
        return this.dataset.backgroundMobileUrl;
      }

      return this.dataset.backgroundUrl || this.originalBackgroundUrl;
    }

    applyBackground() {
      if (!this.scene) {
        return;
      }

      const backgroundUrl = this.getBackgroundUrl();

      if (!backgroundUrl || backgroundUrl === this.appliedBackgroundUrl) {
        return;
      }

      if (this.backgroundLayer && typeof this.backgroundLayer.setSource === 'function') {
        this.backgroundLayer.setSource(backgroundUrl);
      } else {
        this.scene.setTexture(
          this.dataset.backgroundLayerId,
          'uSourceImage',
          backgroundUrl
        );
      }

      this.appliedBackgroundUrl = backgroundUrl;
    }

    initializeEntranceAnimation() {
      if (typeof window.pgCreateEntranceTimeline !== 'function' || this.introTimeline) {
        return;
      }

      const bounds = this.getBoundingClientRect();

      if (bounds.bottom <= 0) {
        return;
      }

      this.introTimeline = window.pgCreateEntranceTimeline(this, {
        style: this.dataset.animationStyle,
        duration: this.dataset.animationDuration,
        delay: this.dataset.animationDelay,
        stagger: this.dataset.animationStagger,
        distance: this.dataset.animationDistance
      });

      if (!this.introTimeline) {
        return;
      }

      if (bounds.top < window.innerHeight * 0.85) {
        this.introTimeline.play();
        return;
      }

      if (window.ScrollTrigger) {
        this.introTrigger = window.ScrollTrigger.create({
          trigger: this,
          start: 'top 80%',
          once: true,
          animation: this.introTimeline
        });
        return;
      }

      this.introObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.introTimeline?.play();
        this.introObserver?.disconnect();
      }, { rootMargin: '0px 0px -20% 0px' });

      this.introObserver.observe(this);
    }
  }

  if (!customElements.get('pg-liquid-glass-hero')) {
    customElements.define('pg-liquid-glass-hero', PGLiquidGlassHero);
  }
})();
